import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Mail, Copy, Plus, CheckSquare, Circle, Clock, CheckCircle2, AlertTriangle, FileBox, Lightbulb, Milestone, Layers, Sparkles, MoreHorizontal, Pencil, Trash2, RotateCcw, Calendar, User, Inbox, X } from 'lucide-react';
import { TaskData } from '../lib/types';
const linkedTypeIconMap = {
  artifact: FileBox,
  criterion: Lightbulb,
  milestone: Milestone,
  overall: Layers
};
const linkedTypeColorMap = {
  artifact: 'text-indigo-600 bg-indigo-50',
  criterion: 'text-purple-600 bg-purple-50',
  milestone: 'text-amber-600 bg-amber-50',
  overall: 'text-slate-600 bg-slate-100'
};
const statusConfig = {
  open: {
    label: 'Open',
    icon: Circle,
    iconCls: 'text-foreground',
    bgCls: 'bg-slate-100'
  },
  'in-progress': {
    label: 'In Progress',
    icon: Clock,
    iconCls: 'text-indigo-600',
    bgCls: 'bg-indigo-50'
  },
  complete: {
    label: 'Completed',
    icon: CheckCircle2,
    iconCls: 'text-emerald-600',
    bgCls: 'bg-emerald-50'
  }
};
interface TasksTabProps {
  tasks: TaskData[];
  onAddTask?: (task: TaskData) => void;
  inboxEmail?: string;
}
export function TasksTab({
  tasks,
  onAddTask,
  inboxEmail = 'nih-r01-alzheimers-2026@origamigrants.com'
}: TasksTabProps) {
  const [openMoreId, setOpenMoreId] = useState<string | null>(null);
  const [showInboxModal, setShowInboxModal] = useState(false);
  const [showAddTaskModal, setShowAddTaskModal] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    summary: '',
    owner: '',
    dueDate: '',
    linkedType: 'overall' as TaskData['linkedType'],
    linkedName: ''
  });
  const moreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setOpenMoreId(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  if (tasks.length === 0) {
    return <Card className="border-0 shadow-sm h-full" data-id="element-567">
        <CardContent className="p-0 flex flex-col h-full" data-id="element-568">
          {/* Summary Stats Bar */}
          <div className="px-5 py-3 flex items-center justify-between border-b border-border bg-white flex-shrink-0 sticky top-0 z-10 rounded-t-lg" data-id="element-569">
            <div className="flex items-center gap-4 text-xs" data-id="element-570">
              <span className="font-medium text-red-600 flex items-center gap-1.5" data-id="element-571">
                <AlertTriangle className="h-3.5 w-3.5" data-id="element-572" />0 overdue
              </span>
              <span className="font-medium text-foreground" data-id="element-573">0 open</span>
              <span className="font-medium text-indigo-600" data-id="element-574">0 in progress</span>
              <span className="font-medium text-emerald-600" data-id="element-575">0 completed</span>
            </div>
            <div className="flex items-center gap-2" data-id="element-576">
              <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => setShowInboxModal(true)} data-id="element-577">
                <Inbox className="h-3.5 w-3.5" data-id="element-578" />
                Set up
                <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-0.5 shadow-none font-medium ml-0.5" data-id="element-579">
                  <Sparkles className="h-3 w-3" data-id="element-580" />
                  AI Inbox
                </Badge>
              </Button>
              <Button size="sm" className="h-8 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowAddTaskModal(true)} data-id="element-581">
                <Plus className="h-3.5 w-3.5" data-id="element-582" />
                Add Task
              </Button>
            </div>
          </div>

          {/* Inbox Modal */}
          {showInboxModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowInboxModal(false)} data-id="element-583">
              <div className="bg-white rounded-lg border shadow-xl w-[400px] p-5" onClick={e => e.stopPropagation()} data-id="element-584">
                <div className="flex items-center justify-between mb-4" data-id="element-585">
                  <div className="flex items-center gap-2" data-id="element-586">
                    <div className="p-1.5 rounded-md bg-indigo-50 border border-indigo-100" data-id="element-587">
                      <Mail className="h-4 w-4 text-indigo-600" data-id="element-588" />
                    </div>
                    <span className="font-semibold text-foreground text-sm" data-id="element-589">
                      Proposal Inbox
                    </span>
                    <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-0.5 shadow-none font-medium" data-id="element-590">
                      <Sparkles className="h-3 w-3" data-id="element-591" />
                      AI Inbox
                    </Badge>
                  </div>
                  <button onClick={() => setShowInboxModal(false)} className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted text-muted-foreground transition-colors" data-id="element-592">
                    <X className="h-4 w-4" data-id="element-593" />
                  </button>
                </div>
                <p className="text-xs text-muted-foreground mb-3" data-id="element-594">
                  This proposal's unique inbox address:
                </p>
                <div className="flex items-center gap-2 mb-4" data-id="element-595">
                  <code className="flex-1 text-xs text-indigo-600 font-mono bg-indigo-50/50 px-2.5 py-2 rounded-md border border-indigo-100" data-id="element-596">
                    {inboxEmail}
                  </code>
                  <Button variant="outline" size="sm" className="h-8 px-3 text-xs gap-1.5" data-id="element-597">
                    <Copy className="h-3 w-3" data-id="element-598" />
                    Copy
                  </Button>
                </div>
                <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground leading-relaxed" data-id="element-599">
                  <p className="font-medium text-foreground mb-1" data-id="element-600">
                    How it works
                  </p>
                  <ul className="space-y-1 list-disc list-inside" data-id="element-601">
                    <li data-id="element-602">Forward, CC, or send emails to this address</li>
                    <li data-id="element-603">
                      AI will parse the email and create / update tasks for you
                    </li>
                    <li data-id="element-604">
                      Replies to task notifications update the task as well
                    </li>
                  </ul>
                </div>
              </div>
            </div>}

          {/* Add Task Modal */}
          {showAddTaskModal && <AddTaskModal onClose={() => setShowAddTaskModal(false)} newTask={newTask} setNewTask={setNewTask} onAddTask={onAddTask} data-id="element-605" />}

          {/* Empty state placeholder */}
          <div className="flex-1 flex flex-col items-center justify-center text-center p-12" data-id="element-606">
            <div className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4" data-id="element-607">
              <CheckSquare className="h-6 w-6 text-slate-400" data-id="element-608" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2" data-id="element-609">
              No tasks yet
            </h3>
            <p className="text-sm text-muted-foreground max-w-md" data-id="element-610">
              Tasks will appear here as you identify work items or when AI
              suggests actions based on your proposal requirements.
            </p>
          </div>
        </CardContent>
      </Card>;
  }
  // Group tasks by status
  const openTasks = tasks.filter(t => t.status === 'open');
  const inProgressTasks = tasks.filter(t => t.status === 'in-progress');
  const completedTasks = tasks.filter(t => t.status === 'complete');
  // Sort by due date ascending (earliest/overdue first)
  const sortByDueDate = (a: TaskData, b: TaskData) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  const columns = [{
    id: 'open',
    title: 'Open',
    tasks: openTasks.sort(sortByDueDate),
    status: 'open' as const
  }, {
    id: 'in-progress',
    title: 'In Progress',
    tasks: inProgressTasks.sort(sortByDueDate),
    status: 'in-progress' as const
  }, {
    id: 'complete',
    title: 'Completed',
    tasks: completedTasks.sort(sortByDueDate),
    status: 'complete' as const
  }];
  return <Card className="border-0 shadow-sm flex flex-col h-full" data-id="element-611">
      <CardContent className="p-0 flex flex-col h-full" data-id="element-612">
        {/* Summary Stats Bar */}
        <div className="px-5 py-3 flex items-center justify-between border-b border-border bg-white flex-shrink-0 sticky top-0 z-10 rounded-t-lg" data-id="element-613">
          <div className="flex items-center gap-4 text-xs" data-id="element-614">
            <span className="font-medium text-red-600 flex items-center gap-1.5" data-id="element-615">
              <AlertTriangle className="h-3.5 w-3.5" data-id="element-616" />
              {tasks.filter(t => t.isOverdue && t.status !== 'complete').length}{' '}
              overdue
            </span>
            <span className="font-medium text-foreground" data-id="element-617">
              {openTasks.length} open
            </span>
            <span className="font-medium text-indigo-600" data-id="element-618">
              {inProgressTasks.length} in progress
            </span>
            <span className="font-medium text-emerald-600" data-id="element-619">
              {completedTasks.length} completed
            </span>
          </div>
          <div className="flex items-center gap-2" data-id="element-620">
            <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={() => setShowInboxModal(true)} data-id="element-621">
              <Inbox className="h-3.5 w-3.5" data-id="element-622" />
              Set up
              <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-0.5 shadow-none font-medium ml-0.5" data-id="element-623">
                <Sparkles className="h-3 w-3" data-id="element-624" />
                AI Inbox
              </Badge>
            </Button>
            <Button size="sm" className="h-8 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700" onClick={() => setShowAddTaskModal(true)} data-id="element-625">
              <Plus className="h-3.5 w-3.5" data-id="element-626" />
              Add Task
            </Button>
          </div>
        </div>

        {/* Inbox Modal */}
        {showInboxModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowInboxModal(false)} data-id="element-627">
            <div className="bg-white rounded-lg border shadow-xl w-[400px] p-5" onClick={e => e.stopPropagation()} data-id="element-628">
              <div className="flex items-center justify-between mb-4" data-id="element-629">
                <div className="flex items-center gap-2" data-id="element-630">
                  <div className="p-1.5 rounded-md bg-indigo-50 border border-indigo-100" data-id="element-631">
                    <Mail className="h-4 w-4 text-indigo-600" data-id="element-632" />
                  </div>
                  <span className="font-semibold text-foreground text-sm" data-id="element-633">
                    Proposal Inbox
                  </span>
                  <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-0.5 shadow-none font-medium" data-id="element-634">
                    <Sparkles className="h-3 w-3" data-id="element-635" />
                    AI Inbox
                  </Badge>
                </div>
                <button onClick={() => setShowInboxModal(false)} className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted text-muted-foreground transition-colors" data-id="element-636">
                  <X className="h-4 w-4" data-id="element-637" />
                </button>
              </div>
              <p className="text-xs text-muted-foreground mb-3" data-id="element-638">
                This proposal's unique inbox address:
              </p>
              <div className="flex items-center gap-2 mb-4" data-id="element-639">
                <code className="flex-1 text-xs text-indigo-600 font-mono bg-indigo-50/50 px-2.5 py-2 rounded-md border border-indigo-100" data-id="element-640">
                  {inboxEmail}
                </code>
                <Button variant="outline" size="sm" className="h-8 px-3 text-xs gap-1.5" data-id="element-641">
                  <Copy className="h-3 w-3" data-id="element-642" />
                  Copy
                </Button>
              </div>
              <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground leading-relaxed" data-id="element-643">
                <p className="font-medium text-foreground mb-1" data-id="element-644">How it works</p>
                <ul className="space-y-1 list-disc list-inside" data-id="element-645">
                  <li data-id="element-646">Forward, CC, or send emails to this address</li>
                  <li data-id="element-647">
                    AI will parse the email and create / update tasks for you
                  </li>
                  <li data-id="element-648">Replies to task notifications update the task as well</li>
                </ul>
              </div>
            </div>
          </div>}

        {/* Add Task Modal */}
        {showAddTaskModal && <AddTaskModal onClose={() => setShowAddTaskModal(false)} newTask={newTask} setNewTask={setNewTask} onAddTask={onAddTask} data-id="element-649" />}

        {/* Kanban Board */}
        <div className="flex-1 min-h-0 bg-muted/30 p-5 overflow-x-auto" data-id="element-650">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 min-w-[800px] h-full" data-id="element-651">
            {columns.map(column => {
            const StatusIcon = statusConfig[column.status].icon;
            const iconCls = statusConfig[column.status].iconCls;
            return <div key={column.id} className="flex flex-col h-full min-h-0" data-id="element-652">
                  {/* Column Header */}
                  <div className="flex items-center justify-between mb-3 px-1" data-id="element-653">
                    <div className="flex items-center gap-2" data-id="element-654">
                      <StatusIcon className={`h-4 w-4 ${iconCls}`} data-id="element-655" />
                      <span className="font-semibold text-sm text-foreground" data-id="element-656">
                        {column.title}
                      </span>
                      <Badge variant="secondary" className="h-5 px-1.5 min-w-[20px] justify-center text-[10px]" data-id="element-657">
                        {column.tasks.length}
                      </Badge>
                    </div>
                  </div>

                  {/* Task List */}
                  <div className="flex-1 overflow-y-auto pr-2 space-y-3 pb-2" data-id="element-658">
                    {column.tasks.map(task => {
                  const LinkedIcon = linkedTypeIconMap[task.linkedType];
                  const isCompleted = task.status === 'complete';
                  return <div key={task.id} className={`group bg-white border border-border rounded-lg p-3 shadow-sm hover:shadow-md transition-all ${isCompleted ? 'opacity-75' : ''}`} data-id="element-659">
                          {/* Header: Title + Actions */}
                          <div className="flex items-start justify-between gap-2 mb-2" data-id="element-660">
                            <h3 className={`text-sm font-medium leading-snug ${isCompleted ? 'text-muted-foreground line-through' : 'text-foreground'}`} data-id="element-661">
                              {task.title}
                            </h3>
                            <div className="relative" ref={openMoreId === task.id ? moreRef : undefined} data-id="element-662">
                              <button onClick={e => {
                          e.stopPropagation();
                          setOpenMoreId(openMoreId === task.id ? null : task.id);
                        }} className="opacity-0 group-hover:opacity-100 focus:opacity-100 inline-flex items-center justify-center h-6 w-6 rounded-md hover:bg-muted text-muted-foreground transition-all" data-id="element-663">
                                <MoreHorizontal className="h-3.5 w-3.5" data-id="element-664" />
                              </button>
                              {openMoreId === task.id && <div className="absolute top-full right-0 mt-1 z-50 w-32 rounded-md bg-white border shadow-lg py-1 text-left" data-id="element-665">
                                  {!isCompleted ? <>
                                      <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-emerald-600 hover:bg-emerald-50 transition-colors" data-id="element-666">
                                        <CheckCircle2 className="h-3 w-3" data-id="element-667" />
                                        Complete
                                      </button>
                                      <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors" data-id="element-668">
                                        <Pencil className="h-3 w-3" data-id="element-669" />
                                        Edit
                                      </button>
                                    </> : <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-foreground hover:bg-muted transition-colors" data-id="element-670">
                                      <RotateCcw className="h-3 w-3" data-id="element-671" />
                                      Reopen
                                    </button>}
                                  <button onClick={() => setOpenMoreId(null)} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors" data-id="element-672">
                                    <Trash2 className="h-3 w-3" data-id="element-673" />
                                    Delete
                                  </button>
                                </div>}
                            </div>
                          </div>

                          {/* Summary */}
                          <p className="text-xs text-muted-foreground leading-relaxed mb-3 line-clamp-2" data-id="element-674">
                            {task.summary}
                          </p>

                          {/* Badges Row */}
                          <div className="flex flex-wrap items-center gap-2 mb-3" data-id="element-675">
                            {task.source === 'manual' && <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100 border-0 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none font-medium" data-id="element-676">
                                Manual
                              </Badge>}
                            {task.source === 'ai' && <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none font-medium" data-id="element-677">
                                <Sparkles className="h-3 w-3" data-id="element-678" />
                                AI Inbox
                              </Badge>}
                            {task.source === 'email' && <Badge className="bg-violet-50 text-violet-700 hover:bg-violet-50 border border-violet-200 text-[10px] px-1.5 py-0 h-5 gap-1 shadow-none font-medium" data-id="element-679">
                                <Sparkles className="h-3 w-3" data-id="element-680" />
                                AI Inbox
                              </Badge>}

                            <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0 h-5 rounded max-w-full whitespace-nowrap overflow-hidden text-ellipsis ${linkedTypeColorMap[task.linkedType]}`} data-id="element-681">
                              <LinkedIcon className="h-3 w-3 flex-shrink-0" data-id="element-682" />
                              <span className="truncate max-w-[100px]" data-id="element-683">
                                {task.linkedName}
                              </span>
                            </span>
                          </div>

                          {/* Footer: Owner + Due Date */}
                          <div className="flex items-center justify-between pt-2 border-t border-border/50" data-id="element-684">
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground" data-id="element-685">
                              <User className="h-3 w-3" data-id="element-686" />
                              <span className="truncate max-w-[100px]" data-id="element-687">
                                {task.owner}
                              </span>
                            </div>

                            <div className={`flex items-center gap-1.5 text-xs font-medium ${isCompleted ? 'text-muted-foreground' : task.isOverdue ? 'text-red-600' : task.isDueSoon ? 'text-amber-600' : 'text-muted-foreground'}`} data-id="element-688">
                              {task.isOverdue && !isCompleted ? <AlertTriangle className="h-3 w-3 flex-shrink-0 text-red-500" data-id="element-689" /> : <Calendar className="h-3 w-3 flex-shrink-0" data-id="element-690" />}
                              <span data-id="element-691">{task.dueDate}</span>
                            </div>
                          </div>
                        </div>;
                })}
                  </div>
                </div>;
          })}
          </div>
        </div>
      </CardContent>
    </Card>;
}
function AddTaskModal({
  onClose,
  newTask,
  setNewTask,
  onAddTask
}: {
  onClose: () => void;
  newTask: {
    title: string;
    summary: string;
    owner: string;
    dueDate: string;
    linkedType: TaskData['linkedType'];
    linkedName: string;
  };
  setNewTask: React.Dispatch<React.SetStateAction<typeof newTask>>;
  onAddTask?: (task: TaskData) => void;
}) {
  const handleSubmit = () => {
    if (!newTask.title.trim()) return;
    const task: TaskData = {
      id: `t-${Date.now()}`,
      title: newTask.title,
      summary: newTask.summary,
      owner: newTask.owner || 'Unassigned',
      ownerRole: '',
      dueDate: newTask.dueDate || 'No date',
      status: 'open',
      source: 'manual',
      linkedType: newTask.linkedType,
      linkedName: newTask.linkedName || 'Overall',
      isOverdue: false,
      isDueSoon: false
    };
    onAddTask?.(task);
    setNewTask({
      title: '',
      summary: '',
      owner: '',
      dueDate: '',
      linkedType: 'overall',
      linkedName: ''
    });
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose} data-id="element-692">
      <div className="bg-white rounded-lg border shadow-xl w-[480px] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-693">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border" data-id="element-694">
          <div className="flex items-center gap-2" data-id="element-695">
            <div className="p-1.5 rounded-md bg-indigo-50 border border-indigo-100" data-id="element-696">
              <Plus className="h-4 w-4 text-indigo-600" data-id="element-697" />
            </div>
            <span className="font-semibold text-foreground text-sm" data-id="element-698">
              Add Task
            </span>
          </div>
          <button onClick={onClose} className="inline-flex items-center justify-center h-7 w-7 rounded-md hover:bg-muted text-muted-foreground transition-colors" data-id="element-699">
            <X className="h-4 w-4" data-id="element-700" />
          </button>
        </div>

        {/* Form */}
        <div className="px-5 py-4 space-y-4" data-id="element-701">
          {/* Title */}
          <div data-id="element-702">
            <label className="block text-xs font-medium text-foreground mb-1.5" data-id="element-703">
              Title <span className="text-red-500" data-id="element-704">*</span>
            </label>
            <input type="text" value={newTask.title} onChange={e => setNewTask(prev => ({
            ...prev,
            title: e.target.value
          }))} placeholder="e.g., Collect letter of support from Dr. Smith" className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1" data-id="element-705" />
          </div>

          {/* Summary */}
          <div data-id="element-706">
            <label className="block text-xs font-medium text-foreground mb-1.5" data-id="element-707">
              Description
            </label>
            <textarea value={newTask.summary} onChange={e => setNewTask(prev => ({
            ...prev,
            summary: e.target.value
          }))} placeholder="Add details about this task..." rows={3} className="w-full px-3 py-2 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 resize-none" data-id="element-708" />
          </div>

          {/* Owner + Due Date row */}
          <div className="grid grid-cols-2 gap-3" data-id="element-709">
            <div data-id="element-710">
              <label className="block text-xs font-medium text-foreground mb-1.5" data-id="element-711">
                <span className="flex items-center gap-1" data-id="element-712">
                  <User className="h-3 w-3" data-id="element-713" />
                  Assignee
                </span>
              </label>
              <input type="text" value={newTask.owner} onChange={e => setNewTask(prev => ({
              ...prev,
              owner: e.target.value
            }))} placeholder="e.g., Dr. Sarah Chen" className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1" data-id="element-714" />
            </div>
            <div data-id="element-715">
              <label className="block text-xs font-medium text-foreground mb-1.5" data-id="element-716">
                <span className="flex items-center gap-1" data-id="element-717">
                  <Calendar className="h-3 w-3" data-id="element-718" />
                  Due Date
                </span>
              </label>
              <input type="date" value={newTask.dueDate} onChange={e => setNewTask(prev => ({
              ...prev,
              dueDate: e.target.value
            }))} className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1" data-id="element-719" />
            </div>
          </div>

          {/* Linked Type + Name */}
          <div className="grid grid-cols-2 gap-3" data-id="element-720">
            <div data-id="element-721">
              <label className="block text-xs font-medium text-foreground mb-1.5" data-id="element-722">
                Refers To
              </label>
              <select value={newTask.linkedType} onChange={e => setNewTask(prev => ({
              ...prev,
              linkedType: e.target.value as TaskData['linkedType']
            }))} className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1" data-id="element-723">
                <option value="overall" data-id="element-724">Overall</option>
                <option value="artifact" data-id="element-725">Document</option>
                <option value="criterion" data-id="element-726">Criterion</option>
                <option value="milestone" data-id="element-727">Milestone</option>
              </select>
            </div>
            <div data-id="element-728">
              <label className="block text-xs font-medium text-foreground mb-1.5" data-id="element-729">
                Item Name
              </label>
              <input type="text" value={newTask.linkedName} onChange={e => setNewTask(prev => ({
              ...prev,
              linkedName: e.target.value
            }))} placeholder="e.g., Research Strategy" className="w-full h-9 px-3 text-sm rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1" data-id="element-730" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-muted/30" data-id="element-731">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={onClose} data-id="element-732">
            Cancel
          </Button>
          <Button size="sm" className="h-8 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700" onClick={handleSubmit} disabled={!newTask.title.trim()} data-id="element-733">
            <Plus className="h-3.5 w-3.5" data-id="element-734" />
            Add Task
          </Button>
        </div>
      </div>
    </div>;
}